import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FiSearch,
  FiClock,
  FiCalendar,
  FiUser,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiPlus,
  FiActivity,
  FiAlertTriangle,
  FiClipboard,
  FiHeart,
  FiX,
} from 'react-icons/fi';
import dayjs from 'dayjs';
import cn from 'classnames';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

// --- UI Components ---
import { Modal } from '../../ComponnentsUI/common/Modal';
import { Button } from '../../ComponnentsUI/common/Button';
import { AddPatientForm } from '../../ComponnentsUI/features/AddPatientForm';

// --- API & State ---
import {
  appointmentService,
  appointmentStartsInSlot,
  buildDaySlotTimes,
  buildLocalDateTime,
  dedupeAppointments,
  getApiErrorMessage,
  parseAppointmentTime,
  patientHasScheduleConflict,
  toApiDateTime,
  type AppointmentRead,
  type AppointmentTypeValue,
} from '../../../helpers/api/appointment/appointment';
import { appointmentKeys } from '../../../helpers/api/appointment/appointment-query-keys';
import { usePaginatedPatients } from '../../../helpers/api/patients/patients';
import { useMe, usePreferenceUser } from '../../../helpers/api/users/users';
import { usePermissions } from '../../../helpers/acl/permissions';
import {
  isSchedulerUser,
  needsAssigneePicker as needsAssigneePickerForUser,
} from '../../../helpers/acl/schedulingRoles';
import { apiClient } from '../../../helpers/apiHelper';
import {
  facilityProcedureService,
  type FacilityProcedure,
} from '../../../helpers/api/facility-procedure/facility-procedure';

const TYPE_ICONS: Record<string, string> = {
  t1: 'heart',
  t2: 'activity',
  t3: 'activity',
  routine: 'activity',
  emergency: 'alert',
  gyn: 'heart',
  admin: 'clipboard',
};

const TRIMESTER_IDS = new Set(['t1', 't2', 't3']);

function visitTypeKey(proc: Pick<FacilityProcedure, 'code' | 'appointment_type'>) {
  return String(proc.appointment_type || proc.code || '')
    .trim()
    .toLowerCase();
}

function trimesterFromGaWeeks(weeks: number): AppointmentTypeValue {
  if (weeks < 14) return 't1';
  if (weeks < 28) return 't2';
  return 't3';
}

function gaWeeksAt(lmpIso: string, atDate: string): number | null {
  const lmp = dayjs(lmpIso);
  const at = dayjs(atDate);
  if (!lmp.isValid() || !at.isValid()) return null;
  return Math.max(0, Math.floor(at.diff(lmp, 'day') / 7));
}

function TypeIcon({ name, className }: { name: string; className?: string }) {
  if (name === 'alert') return <FiAlertTriangle className={className} size={18} />;
  if (name === 'clipboard') return <FiClipboard className={className} size={18} />;
  if (name === 'heart') return <FiHeart className={className} size={18} />;
  return <FiActivity className={className} size={18} />;
}

const PRIORITIES = ['ROUTINE', 'URGENT', 'STAT'] as const;

type BookingWizardModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialDateTime?: dayjs.Dayjs | null;
  initialAssigneeId?: string | null;
  appointmentToEdit?: AppointmentRead | null;
  onSuccess?: () => void;
};

const BookingWizardModal = ({
  isOpen,
  onClose,
  initialDateTime,
  initialAssigneeId = null,
  appointmentToEdit = null,
  onSuccess,
}: BookingWizardModalProps) => {
  const { t } = useTranslation('Scheduling');
  const queryClient = useQueryClient();
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const addPatientFormRef = useRef<any>(null);
  const wasOpenRef = useRef(false);
  const userPickedTimeRef = useRef(false);
  const isEditing = Boolean(appointmentToEdit?.id);
  
  const PAGE_SIZE = 5;

  const { data: userResp } = useMe();
  const user = userResp?.data || userResp;
  const isScheduler = isSchedulerUser(user);
  const needsAssigneePicker = needsAssigneePickerForUser(user);
  const { can, ready: permissionsReady } = usePermissions();
  const canReadPatients = can('patient.read');
  const canCreatePatients = can('patient.create');
  const { data: prefsResponse } = usePreferenceUser();
  /** Visit length and grid step — both from user preferences. */
  const sessionDuration = prefsResponse?.data?.session_duration || 30;

  useEffect(() => {
    if (permissionsReady && !canCreatePatients && isCreatingNew) {
      setIsCreatingNew(false);
    }
  }, [permissionsReady, canCreatePatients, isCreatingNew]);

  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    patient: null as any,
    type: {
      id: '' as string,
      icon: 'activity',
      label: '',
      duration: 30,
    },
    facilityProcedureId: null as number | null,
    date: dayjs().format('YYYY-MM-DD'),
    time: '09:00',
    reason: '',
    priority: 'ROUTINE' as (typeof PRIORITIES)[number],
  });

  const appointmentDuration =
    formData.type.duration || sessionDuration;

  const patientIdForPregnancy = formData.patient?.id
    ? String(formData.patient.id)
    : '';

  const { data: activePregnancy } = useQuery({
    queryKey: ['booking-active-pregnancy', patientIdForPregnancy],
    queryFn: async () => {
      const res: any = await apiClient.get(
        `/pregnancy/latest/${patientIdForPregnancy}`
      );
      // Interceptor unwraps axios → { data: { pregnancy, ... } } or nested.
      const payload = res?.data?.data ?? res?.data ?? res ?? null;
      if (!payload || typeof payload !== 'object') return null;
      // Explicit empty pregnancy from API
      if (payload.pregnancy === null) return null;
      const preg =
        payload.pregnancy && typeof payload.pregnancy === 'object'
          ? payload.pregnancy
          : payload.last_menstrual_period || payload.estimated_due_date
            ? payload
            : null;
      if (!preg || typeof preg !== 'object') return null;
      // Prefer medically active; still allow GA lock when LMP exists
      if (preg.is_medically_active === false) return null;
      const status = String(preg.pregnancy_status || '').toLowerCase();
      if (status && status !== 'active') return null;
      return preg as {
        pregnancy_status?: string;
        is_medically_active?: boolean;
        last_menstrual_period?: string;
        estimated_due_date?: string;
        current_gestational_age_weeks?: number;
      };
    },
    enabled: Boolean(patientIdForPregnancy) && isOpen,
    staleTime: 30_000,
  });

  const {
    data: facilityProcedures = [],
    isError: proceduresError,
    isLoading: proceduresLoading,
  } = useQuery({
    queryKey: ['facility-procedures', 'booking', 'visit-types'],
    queryFn: () => facilityProcedureService.list(true, true),
    enabled: isOpen,
    staleTime: 10_000,
  });

  /** Classification options — backend visit types only (defaults + MWL-imported). */
  const classificationProcedures = useMemo(
    () =>
      [...facilityProcedures].sort((a, b) =>
        String(a.description).localeCompare(String(b.description))
      ),
    [facilityProcedures]
  );

  const pregnancyGaAtDate = useMemo(() => {
    if (!activePregnancy) return null;
    let lmp = activePregnancy.last_menstrual_period;
    if (!lmp && activePregnancy.estimated_due_date) {
      lmp = dayjs(activePregnancy.estimated_due_date)
        .subtract(280, 'day')
        .toISOString();
    }
    if (!lmp) return null;
    const weeks = gaWeeksAt(lmp, formData.date);
    if (weeks == null) return null;
    return weeks;
  }, [activePregnancy, formData.date]);

  const allowedTrimester = useMemo((): AppointmentTypeValue | null => {
    if (pregnancyGaAtDate == null) return null;
    return trimesterFromGaWeeks(pregnancyGaAtDate);
  }, [pregnancyGaAtDate]);

  const procedureTypeOf = (proc: FacilityProcedure): string =>
    visitTypeKey(proc) || 'routine';

  const typeMeta = (typeId: string, fallbackLabel?: string) => {
    const proc = classificationProcedures.find(
      (p) => visitTypeKey(p) === typeId
    );
    return {
      id: typeId,
      icon: TYPE_ICONS[typeId] || 'activity',
      label:
        proc?.description ||
        fallbackLabel ||
        t(`modal.scanTypes.${typeId}`, { defaultValue: typeId }),
      duration: sessionDuration,
    };
  };

  const emptyClassification = useMemo(
    () => ({
      id: '',
      icon: 'activity',
      label: '',
      duration: sessionDuration,
    }),
    [sessionDuration]
  );

  // Pregnancy GA at the visit date locks classification to the matching trimester.
  useEffect(() => {
    if (!allowedTrimester) return;
    if (!classificationProcedures.length) return;
    if (!patientIdForPregnancy) return;

    const matchingProc =
      classificationProcedures.find(
        (p) => procedureTypeOf(p) === allowedTrimester
      ) || null;
    if (!matchingProc) return;

    setFormData((prev) => {
      const alreadyOnTrimester =
        prev.facilityProcedureId === matchingProc.id ||
        String(prev.type.id).toLowerCase() === allowedTrimester;
      if (alreadyOnTrimester) return prev;
      return {
        ...prev,
        type: typeMeta(allowedTrimester, matchingProc.description),
        facilityProcedureId: matchingProc.id,
        reason: matchingProc.description || prev.reason,
      };
    });
  }, [
    allowedTrimester,
    classificationProcedures,
    patientIdForPregnancy,
    sessionDuration,
    t,
  ]);

  // On patient change (create flow): clear classification; GA effect may re-select trimester.
  const prevPatientIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      prevPatientIdRef.current = null;
      return;
    }
    if (appointmentToEdit?.id) {
      prevPatientIdRef.current = patientIdForPregnancy || null;
      return;
    }
    const nextId = patientIdForPregnancy || null;
    const prevId = prevPatientIdRef.current;
    prevPatientIdRef.current = nextId;
    if (prevId === nextId) return;

    setFormData((prev) => ({
      ...prev,
      facilityProcedureId: null,
      type: emptyClassification,
      reason: '',
    }));
  }, [
    isOpen,
    appointmentToEdit?.id,
    patientIdForPregnancy,
    emptyClassification,
  ]);

  // Keep selected type label/duration in sync with backend catalog (only if already selected).
  useEffect(() => {
    if (!classificationProcedures.length) return;
    setFormData((prev) => {
      if (prev.facilityProcedureId == null) return prev;
      const byId = classificationProcedures.find(
        (p) => p.id === prev.facilityProcedureId
      );
      if (!byId) {
        return {
          ...prev,
          facilityProcedureId: null,
          type: emptyClassification,
        };
      }
      const next = typeMeta(procedureTypeOf(byId), byId.description);
      if (
        next.id === prev.type.id &&
        next.label === prev.type.label &&
        next.duration === prev.type.duration
      ) {
        return prev;
      }
      return {
        ...prev,
        type: next,
        facilityProcedureId: byId.id,
      };
    });
  }, [classificationProcedures, sessionDuration, t, emptyClassification]);

  const { data: assignees = [] } = useQuery({
    queryKey: appointmentKeys.bookableDoctors(),
    queryFn: appointmentService.getBookableDoctors,
    enabled: isOpen && needsAssigneePicker,
  });

  const resolvedAssigneeId = needsAssigneePicker ? selectedAssigneeId : user?.id;

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }

    const opening = !wasOpenRef.current;
    wasOpenRef.current = true;

    if (!needsAssigneePicker && user?.id) {
      setSelectedAssigneeId(user.id);
      return;
    }

    if (opening) {
      if (isEditing && appointmentToEdit?.doctor_id) {
        setSelectedAssigneeId(String(appointmentToEdit.doctor_id));
      } else if (initialAssigneeId) {
        setSelectedAssigneeId(initialAssigneeId);
      } else if (isScheduler && user?.id && assignees.some((a) => a.id === user.id)) {
        setSelectedAssigneeId(user.id);
      } else if (assignees.length === 1) {
        setSelectedAssigneeId(assignees[0].id);
      }
      return;
    }

    if (
      selectedAssigneeId &&
      assignees.length > 0 &&
      !assignees.some((a) => a.id === selectedAssigneeId)
    ) {
      setSelectedAssigneeId(
        isScheduler && user?.id && assignees.some((a) => a.id === user.id)
          ? user.id
          : null
      );
    }
  }, [
    isOpen,
    needsAssigneePicker,
    isScheduler,
    user?.id,
    assignees,
    selectedAssigneeId,
    initialAssigneeId,
    isEditing,
    appointmentToEdit?.doctor_id,
  ]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!isOpen) {
      userPickedTimeRef.current = false;
      wasOpenRef.current = false;
      return;
    }

    const justOpened = !wasOpenRef.current;
    wasOpenRef.current = true;
    if (!justOpened) return;

    setIsCreatingNew(false);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setCurrentPage(1);

    if (appointmentToEdit?.id) {
      userPickedTimeRef.current = true;
      const start = parseAppointmentTime(appointmentToEdit.start_time);
      const typeId = String(
        appointmentToEdit.appointment_type || ''
      ).toLowerCase();
      const linkedId =
        (appointmentToEdit as { facility_procedure_id?: number | null })
          .facility_procedure_id ?? null;
      const linkedProc =
        (linkedId
          ? classificationProcedures.find((p) => p.id === linkedId)
          : null) ||
        (typeId
          ? classificationProcedures.find((p) => visitTypeKey(p) === typeId)
          : null) ||
        null;
      const typeMatch = linkedProc
        ? typeMeta(procedureTypeOf(linkedProc), linkedProc.description)
        : typeId
          ? typeMeta(typeId)
          : emptyClassification;
      const priorityRaw = String(appointmentToEdit.priority || 'ROUTINE').toUpperCase();
      const priority = (PRIORITIES as readonly string[]).includes(priorityRaw)
        ? (priorityRaw as (typeof PRIORITIES)[number])
        : 'ROUTINE';

      setFormData({
        patient: {
          id: appointmentToEdit.patient_id,
          patient_name: appointmentToEdit.patient_name || '',
          patient_last_name: appointmentToEdit.patient_last_name || '',
          patient_mrn: appointmentToEdit.patient_mrn || null,
        },
        type: typeMatch,
        facilityProcedureId: linkedProc?.id ?? linkedId,
        date: start.format('YYYY-MM-DD'),
        time: start.format('HH:mm'),
        reason: appointmentToEdit.reason || linkedProc?.description || '',
        priority,
      });
      return;
    }

    userPickedTimeRef.current = !!initialDateTime;
    const dateStr = initialDateTime
      ? initialDateTime.format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD');
    const timeStr = initialDateTime
      ? initialDateTime.format('HH:mm')
      : '09:00';
    setFormData((prev) => ({
      ...prev,
      patient: null,
      type: emptyClassification,
      facilityProcedureId: null,
      date: dateStr,
      time: timeStr,
      reason: '',
      priority: 'ROUTINE',
    }));
  }, [
    isOpen,
    initialDateTime,
    appointmentToEdit,
    classificationProcedures,
    emptyClassification,
  ]);

  const { data: assigneeAgenda } = useQuery({
    queryKey: appointmentKeys.agenda(
      formData.date,
      formData.date,
      resolvedAssigneeId ?? undefined
    ),
    queryFn: () =>
      appointmentService.getDailyAgenda(
        formData.date,
        formData.date,
        resolvedAssigneeId ?? undefined
      ),
    enabled: !!formData.date && isOpen && !!resolvedAssigneeId,
  });

  const { data: allStaffAgenda } = useQuery({
    queryKey: appointmentKeys.agenda(formData.date, formData.date, 'all'),
    queryFn: () => appointmentService.getDailyAgenda(formData.date, formData.date),
    enabled: !!formData.date && isOpen,
  });

  const formatAssigneeLabel = (assignee: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    role?: string;
    id?: string;
  }) => {
    const name = [assignee.first_name, assignee.last_name].filter(Boolean).join(' ').trim();
    const base = name || assignee.email || t('staff');
    if (assignee.id === user?.id) return t('assigneeMe', { name: base });
    return t('assigneeProvider', { name: base });
  };

  const bookedForAssignee = useMemo(() => {
    const raw = Array.isArray(assigneeAgenda) ? assigneeAgenda : [];
    return dedupeAppointments(raw);
  }, [assigneeAgenda]);

  const allAgendaItems = useMemo(() => {
    const raw = Array.isArray(allStaffAgenda) ? allStaffAgenda : [];
    return dedupeAppointments(raw);
  }, [allStaffAgenda]);

  const buildSlotOption = (
    slotStart: dayjs.Dayjs,
    selectedPatientId: string | undefined
  ) => {
    const slotEnd = slotStart.add(sessionDuration, 'minutes');
    const now = dayjs();
    const tStr = slotStart.format('HH:mm');
    const hasVisitStartingHere = bookedForAssignee.some(
      (app) =>
        app.id !== appointmentToEdit?.id &&
        appointmentStartsInSlot(slotStart, slotEnd, app)
    );
    const patientConflict =
      selectedPatientId &&
      patientHasScheduleConflict(
        formData.date,
        selectedPatientId,
        allAgendaItems,
        appointmentToEdit?.id
      );
    const isPast =
      slotStart.isBefore(now, 'minute') &&
      buildLocalDateTime(formData.date, '00:00').isSame(now, 'day');
    return {
      time: tStr,
      isBooked: hasVisitStartingHere,
      isUnavailable: isPast || Boolean(patientConflict),
    };
  };

  const timeOptions = useMemo(() => {
    const slotTimes = buildDaySlotTimes(formData.date, 8, 19, sessionDuration);
    const selectedPatientId = formData.patient?.id as string | undefined;

    const options = slotTimes.map((slotStart) =>
      buildSlotOption(slotStart, selectedPatientId)
    );

    // Keep the grid-clicked time visible even if prefs load with a different step.
    if (
      formData.time &&
      !options.some((slot) => slot.time === formData.time)
    ) {
      options.push(
        buildSlotOption(
          buildLocalDateTime(formData.date, formData.time),
          selectedPatientId
        )
      );
      options.sort((a, b) => a.time.localeCompare(b.time));
    }

    return options;
  }, [
    formData.date,
    formData.time,
    formData.patient?.id,
    appointmentDuration,
    bookedForAssignee,
    allAgendaItems,
    sessionDuration,
    appointmentToEdit?.id,
  ]);

  useEffect(() => {
    // Only auto-pick for "New appointment" (no grid slot). Skip when user clicked a slot.
    if (!isOpen || userPickedTimeRef.current || initialDateTime) return;
    const selected = timeOptions.find((slot) => slot.time === formData.time);
    if (selected && !selected.isUnavailable) return;
    const firstAvailable = timeOptions.find((slot) => !slot.isUnavailable);
    if (firstAvailable && firstAvailable.time !== formData.time) {
      setFormData((prev) => ({ ...prev, time: firstAvailable.time }));
    }
  }, [isOpen, initialDateTime, formData.date, timeOptions, formData.time]);

  const patientFilters = useMemo(() => {
    const filters: Record<string, unknown> = {};
    if (debouncedSearchTerm) {
      filters.condition__or = {
        patient_name__ilike: `%${debouncedSearchTerm}%`,
        patient_last_name__ilike: `%${debouncedSearchTerm}%`,
      };
    }
    return { archived: 'false', filters: JSON.stringify(filters) };
  }, [debouncedSearchTerm]);
  
  const {
    data: searchData,
    isError: isPatientsError,
    isLoading: isPatientsLoading,
    refetch: refetchPatients,
  } = usePaginatedPatients(currentPage, PAGE_SIZE, patientFilters, {
    enabled: isOpen && !isCreatingNew && permissionsReady && canReadPatients,
    onError: (err: unknown) => {
      message.error(getApiErrorMessage(err, t('modal.messages.loadPatientsError')));
    },
  });

  useEffect(() => {
    if (!isOpen || isCreatingNew || !permissionsReady || !canReadPatients) return;
    refetchPatients();
  }, [
    patientFilters,
    currentPage,
    isOpen,
    isCreatingNew,
    permissionsReady,
    canReadPatients,
    refetchPatients,
  ]);

  const patients = searchData?.items || searchData?.data?.items || [];
  const totalPages = searchData?.total_pages || searchData?.data?.total_pages || 1;

  const createMutation = useMutation({
    mutationFn: appointmentService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.agendas() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      message.success(t('modal.messages.scheduleSuccess'));
      if (onSuccess) onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      message.error(getApiErrorMessage(err, t('modal.messages.scheduleError')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof appointmentService.update>[1];
    }) => appointmentService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.agendas() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
      message.success(
        t('modal.messages.updateSuccess', {
          defaultValue: 'Appointment updated',
        })
      );
      if (onSuccess) onSuccess();
      onClose();
    },
    onError: (err: unknown) => {
      message.error(
        getApiErrorMessage(
          err,
          t('modal.messages.updateError', {
            defaultValue: 'Failed to update appointment',
          })
        )
      );
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const handleMainAction = () => {
    if (isSaving) return;
    if (isCreatingNew) return addPatientFormRef.current?.requestSubmit();
    if (!formData.patient) return message.warning(t('modal.messages.selectPatient'));
    if (!formData.facilityProcedureId) {
      return message.warning(
        t('modal.messages.selectClassification', {
          defaultValue: 'Select a classification',
        })
      );
    }
    if (!resolvedAssigneeId) {
      return message.warning(
        isScheduler
          ? t('modal.messages.selectAssignee')
          : t('modal.messages.selectDoctorWarning')
      );
    }
    const start = buildLocalDateTime(formData.date, formData.time);
    const end = start.add(appointmentDuration, 'minutes');
    if (
      patientHasScheduleConflict(
        formData.date,
        formData.patient.id,
        allAgendaItems,
        appointmentToEdit?.id
      )
    ) {
      return message.error(t('modal.messages.sameDayConflict'));
    }
    const clinicalReason =
      formData.reason ||
      (formData.facilityProcedureId
        ? facilityProcedures.find((p) => p.id === formData.facilityProcedureId)
            ?.description || t('modal.defaultReason')
        : t('modal.defaultReason'));

    if (isEditing && appointmentToEdit?.id) {
      updateMutation.mutate({
        id: String(appointmentToEdit.id),
        data: {
          doctor_id: resolvedAssigneeId,
          start_time: toApiDateTime(start),
          end_time: toApiDateTime(end),
          duration_minutes: appointmentDuration,
          appointment_type: formData.type.id,
          reason: clinicalReason,
          priority: formData.priority,
          facility_procedure_id: formData.facilityProcedureId,
        } as any,
      });
      return;
    }

    createMutation.mutate({
      patient_id: formData.patient.id,
      doctor_id: resolvedAssigneeId,
      // Until a dedicated ordering-provider picker exists, default to assignee.
      ordering_provider_id: resolvedAssigneeId,
      start_time: toApiDateTime(start),
      end_time: toApiDateTime(end),
      duration_minutes: appointmentDuration,
      appointment_type: formData.type.id,
      reason: clinicalReason,
      priority: formData.priority,
      facility_procedure_id: formData.facilityProcedureId,
    });
  };

  const canConfirm =
    Boolean(formData.patient) &&
    Boolean(resolvedAssigneeId) &&
    Boolean(formData.facilityProcedureId) &&
    !isSaving;
  const patientReady = Boolean(formData.patient);
  const slotReady = Boolean(formData.date && formData.time && resolvedAssigneeId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      className="lg:h-[min(92vh,900px)] lg:max-w-[1400px] lg:w-[94vw]"
      labelledBy="booking-modal-title"
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white dark:bg-gray-800">
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-slate-50/80 px-4 py-4 dark:border-gray-700 dark:bg-gray-900/40 sm:px-6">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
              <FiCalendar size={18} aria-hidden />
            </span>
            <div className="min-w-0">
              <h2
                id="booking-modal-title"
                className="text-lg font-bold text-slate-900 dark:text-white"
              >
                {isCreatingNew
                  ? t('modal.addNewPatient', { defaultValue: 'Add New Patient' })
                  : isEditing
                    ? t('modal.editTitle', {
                        defaultValue: 'Modify Appointment',
                      })
                    : t('modal.title')}
              </h2>
              <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
                {isCreatingNew
                  ? t('modal.addNewPatientHint', {
                      defaultValue:
                        'Create the patient, then continue booking the appointment',
                    })
                  : isEditing
                    ? t('modal.editHint', {
                        defaultValue:
                          'Update the time, provider, type, or notes for this visit',
                      })
                    : t('modal.durationType', {
                      duration: appointmentDuration,
                      type:
                        (formData.facilityProcedureId
                          ? facilityProcedures.find(
                              (p) => p.id === formData.facilityProcedureId
                            )?.description
                          : null) ||
                        formData.type.label ||
                        '—',
                    })}
              </p>
              {!isCreatingNew && !isEditing && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-wide">
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-1',
                      patientReady
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                    )}
                  >
                    1 · {t('modal.patientDirectory')}
                  </span>
                  <span aria-hidden className="text-slate-300">
                    →
                  </span>
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-1',
                      patientReady
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                        : 'bg-slate-100 text-slate-400 dark:bg-gray-700'
                    )}
                  >
                    2 · {t('modal.classification')}
                  </span>
                  <span aria-hidden className="text-slate-300">
                    →
                  </span>
                  <span
                    className={cn(
                      'rounded-full px-2.5 py-1',
                      slotReady
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                        : 'bg-slate-100 text-slate-400 dark:bg-gray-700'
                    )}
                  >
                    3 · {t('modal.timeSlot')}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-start gap-2">
            {!isCreatingNew && (
              <div
                className={cn(
                  'hidden items-center gap-3 rounded-xl border px-3 py-2 md:flex',
                  formData.patient
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                    : 'border-gray-200 bg-white text-slate-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300'
                )}
              >
                <FiUser size={16} aria-hidden />
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase opacity-70">
                    {t('modal.targetPatient')}
                  </p>
                  <p className="max-w-[160px] truncate text-sm font-semibold">
                    {formData.patient
                      ? `${formData.patient.patient_name} ${formData.patient.patient_last_name}`
                      : t('modal.unselected')}
                  </p>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl p-2.5 text-slate-400 hover:bg-white hover:text-slate-700 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700 dark:hover:text-white"
              aria-label={t('modal.discard')}
            >
              <FiX size={18} />
            </button>
          </div>
        </header>

        <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          {isCreatingNew ? (
            <div className="mx-auto w-full max-w-5xl space-y-4 p-4 sm:p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-slate-500 dark:text-gray-400">
                  {t('modal.addNewPatientHint', {
                    defaultValue:
                      'Create the patient, then continue booking the appointment',
                  })}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsCreatingNew(false)}
                  className="gap-1.5"
                >
                  <FiSearch size={13} />
                  {t('modal.backToSearch', {
                    defaultValue: 'Back to patient search',
                  })}
                </Button>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-5">
                <AddPatientForm
                  ref={addPatientFormRef}
                  onPatientCreated={(p) => {
                    setFormData({ ...formData, patient: p });
                    setIsCreatingNew(false);
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="flex min-h-full flex-col lg:flex-row">
              <div className="flex w-full shrink-0 flex-col border-b border-gray-200 bg-slate-50/70 dark:border-gray-700 dark:bg-gray-900/40 lg:w-[320px] lg:border-b-0 lg:border-r xl:w-[360px]">
                {isEditing ? (
                  <div className="space-y-3 p-4">
                    <h3 className="text-sm font-bold text-slate-800 dark:text-gray-100">
                      {t('modal.targetPatient')}
                    </h3>
                    <div className="rounded-xl border border-blue-200 bg-white p-4 shadow-sm dark:border-blue-800 dark:bg-gray-800">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">
                        {formData.patient?.patient_name}{' '}
                        {formData.patient?.patient_last_name}
                      </p>
                      {formData.patient?.patient_mrn && (
                        <p className="mt-1 text-xs text-slate-400">
                          MRN {formData.patient.patient_mrn}
                        </p>
                      )}
                      <p className="mt-3 text-[11px] text-slate-500 dark:text-gray-400">
                        {t('modal.lockedPatient', {
                          defaultValue: 'Patient cannot be changed when modifying',
                        })}
                      </p>
                    </div>
                  </div>
                ) : (
                <>
                <div className="space-y-3 border-b border-gray-200/80 p-4 dark:border-gray-700">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-800 dark:text-gray-100">
                      {t('modal.patientDirectory')}
                    </h3>
                    {permissionsReady && canCreatePatients && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsCreatingNew(true)}
                        className="gap-1 text-xs"
                      >
                        <FiPlus size={13} />
                        {t('modal.addNew')}
                      </Button>
                    )}
                  </div>
                  {permissionsReady && canReadPatients && (
                    <label className="relative block">
                      <FiSearch
                        size={14}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      />
                      <input
                        className="h-10 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                        placeholder={t('modal.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label={t('modal.searchPlaceholder')}
                      />
                    </label>
                  )}
                </div>

                <div className="space-y-1.5 p-3">
                  {(!permissionsReady || isPatientsLoading) && (
                    <div className="p-8 text-center text-xs font-medium text-slate-400 animate-pulse">
                      {t('modal.loadingPatients')}
                    </div>
                  )}
                  {permissionsReady && !canReadPatients && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center text-xs font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                      {t('modal.patientReadPermissionRequired')}
                    </div>
                  )}
                  {permissionsReady &&
                    canReadPatients &&
                    isPatientsError &&
                    !isPatientsLoading && (
                      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center dark:border-red-800 dark:bg-red-900/20">
                        <p className="mb-2 text-xs text-red-600 dark:text-red-300">
                          {t('modal.loadPatientsError')}
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => refetchPatients()}
                        >
                          {t('modal.retry')}
                        </Button>
                      </div>
                    )}
                  {permissionsReady &&
                    canReadPatients &&
                    !isPatientsLoading &&
                    !isPatientsError &&
                    patients.length === 0 && (
                      <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
                        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-gray-700">
                          <FiUser size={20} />
                        </span>
                        <p className="text-xs font-medium text-slate-400">
                          {t('modal.noPatientsFound')}
                        </p>
                        {canCreatePatients && (
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={() => setIsCreatingNew(true)}
                            className="gap-1"
                          >
                            <FiPlus size={13} />
                            {t('modal.addNew')}
                          </Button>
                        )}
                      </div>
                    )}
                  {permissionsReady &&
                    canReadPatients &&
                    !isPatientsLoading &&
                    !isPatientsError &&
                    patients.map((p: any) => {
                      const selected = formData.patient?.id === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() =>
                            setFormData({ ...formData, patient: p })
                          }
                          className={cn(
                            'flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                            selected
                              ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                              : 'border-transparent bg-white hover:border-blue-200 hover:bg-blue-50/60 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-blue-800 dark:hover:bg-blue-900/20'
                          )}
                        >
                          <div
                            className={cn(
                              'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                              selected
                                ? 'bg-white/20'
                                : 'bg-blue-50 text-blue-700 dark:bg-gray-700 dark:text-blue-300'
                            )}
                          >
                            {(p.patient_name?.[0] || '?').toUpperCase()}
                            {(p.patient_last_name?.[0] || '').toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold">
                              {p.patient_name} {p.patient_last_name}
                            </p>
                            {(p.patient_id || p.mrn) && (
                              <p
                                className={cn(
                                  'mt-0.5 truncate text-[11px]',
                                  selected ? 'text-white/70' : 'text-slate-400'
                                )}
                              >
                                MRN {p.patient_id || p.mrn}
                              </p>
                            )}
                          </div>
                          {selected && (
                            <FiCheckCircle className="shrink-0" size={16} />
                          )}
                        </button>
                      );
                    })}
                </div>

                {canReadPatients && totalPages > 1 && (
                  <div className="m-3 mt-0 flex items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-800">
                    <span className="text-xs font-medium text-slate-500">
                      {t('modal.page', {
                        current: currentPage,
                        total: totalPages,
                      })}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                        className="h-8 w-8 p-0"
                        aria-label={t('actions.prev', {
                          defaultValue: 'Previous',
                        })}
                      >
                        <FiChevronLeft size={16} />
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={currentPage >= totalPages}
                        onClick={() => setCurrentPage((p) => p + 1)}
                        className="h-8 w-8 p-0"
                        aria-label={t('actions.next', {
                          defaultValue: 'Next',
                        })}
                      >
                        <FiChevronRight size={16} />
                      </Button>
                    </div>
                  </div>
                )}
                </>
                )}
              </div>

              <div className="min-w-0 flex-1 space-y-5 p-4 sm:p-5 md:p-6">
                <section>
                  <div className="mb-3 flex items-end justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-800 dark:text-gray-100">
                      {t('modal.classification')}
                    </h3>
                    <span className="text-[11px] font-medium text-slate-400">
                      {appointmentDuration} min
                    </span>
                  </div>

                  {allowedTrimester && pregnancyGaAtDate != null && (
                    <p className="mb-3 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-200">
                      {t('modal.gaTrimesterHint', {
                        defaultValue:
                          'Pregnancy ≈ {{weeks}}w on this date → only {{trimester}} is available',
                        weeks: pregnancyGaAtDate,
                        trimester: t(`modal.scanTypes.${allowedTrimester}`, {
                          defaultValue: allowedTrimester,
                        }),
                      })}
                    </p>
                  )}

                  {proceduresLoading ? (
                    <div className="py-6 text-center text-sm text-slate-400">
                      {t('modal.loadingClassifications', {
                        defaultValue: 'Loading classifications…',
                      })}
                    </div>
                  ) : classificationProcedures.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                      {classificationProcedures.map((proc: FacilityProcedure) => {
                        const selected =
                          formData.facilityProcedureId === proc.id;
                        const mappedType = procedureTypeOf(proc);
                        const matchType = typeMeta(
                          mappedType || 'routine',
                          proc.description
                        );
                        const displayLabel = proc.description;
                        const trimesterLocked =
                          Boolean(allowedTrimester) &&
                          TRIMESTER_IDS.has(mappedType) &&
                          mappedType !== allowedTrimester;
                        return (
                          <button
                            key={`fp-${proc.id}`}
                            type="button"
                            disabled={trimesterLocked}
                            title={
                              trimesterLocked
                                ? t('modal.trimesterLocked', {
                                    defaultValue:
                                      'Not available for gestational age on this date',
                                  })
                                : undefined
                            }
                            onClick={() => {
                              setFormData({
                                ...formData,
                                facilityProcedureId: proc.id,
                                type: matchType,
                                reason: proc.description || '',
                              });
                            }}
                            className={cn(
                              'flex items-center gap-2.5 rounded-xl border p-2.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                              trimesterLocked &&
                                'cursor-not-allowed opacity-40',
                              selected
                                ? 'border-blue-500 bg-blue-50 shadow-sm dark:border-blue-600 dark:bg-blue-900/25'
                                : 'border-gray-200 bg-white hover:border-blue-200 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700'
                            )}
                          >
                            <span
                              className={cn(
                                'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                                selected
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-slate-100 text-slate-500 dark:bg-gray-700 dark:text-gray-300'
                              )}
                            >
                              <TypeIcon name={matchType.icon} />
                            </span>
                            <div className="min-w-0">
                              <p className="truncate text-xs font-semibold leading-snug text-slate-800 dark:text-gray-100">
                                {displayLabel}
                              </p>
                              <p className="mt-0.5 truncate text-[10px] text-slate-400">
                                {trimesterLocked
                                  ? t('modal.trimesterLockedShort', {
                                      defaultValue: 'Wrong trimester',
                                    })
                                  : `${matchType.duration} min`}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm text-slate-400 dark:border-gray-600">
                      {t('modal.noClassifications', {
                        defaultValue:
                          'No visit types for this facility yet. Refresh MWL or select a facility.',
                      })}
                    </p>
                  )}
                  {proceduresError && (
                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                      {t('procedures.loadError', {
                        defaultValue:
                          'Could not load facility procedures. Select a facility and refresh MWL.',
                      })}
                    </p>
                  )}
                </section>

                <section>
                  <h3 className="mb-2 text-sm font-bold text-slate-800 dark:text-gray-100">
                    {t('modal.priority')}
                  </h3>
                  <div className="inline-flex max-w-full flex-wrap rounded-xl border border-gray-200 bg-slate-100 p-1 dark:border-gray-600 dark:bg-gray-700">
                    {PRIORITIES.map((p) => {
                      const selected = formData.priority === p;
                      return (
                        <button
                          key={p}
                          type="button"
                          onClick={() =>
                            setFormData({ ...formData, priority: p })
                          }
                          className={cn(
                            'rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wide transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:px-4',
                            selected
                              ? p === 'STAT'
                                ? 'bg-red-600 text-white shadow-sm'
                                : p === 'URGENT'
                                  ? 'bg-amber-500 text-white shadow-sm'
                                  : 'bg-white text-blue-700 shadow-sm dark:bg-gray-800 dark:text-blue-300'
                              : 'text-slate-500 hover:text-slate-700 dark:text-gray-300'
                          )}
                        >
                          {t(`priority.${p.toLowerCase()}`, {
                            defaultValue: p,
                          })}
                        </button>
                      );
                    })}
                  </div>
                </section>

                <section className="rounded-2xl border border-gray-200 bg-slate-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/30">
                  <h3 className="mb-3 text-sm font-bold text-slate-800 dark:text-gray-100">
                    {t('modal.timeSlot')}
                  </h3>
                  <div className="space-y-4">
                    {needsAssigneePicker && (
                      <div className="space-y-1.5">
                        <label className="block text-xs font-semibold text-slate-600 dark:text-gray-400">
                          {isScheduler ? t('modal.assignTo') : t('modal.doctor')}
                        </label>
                        <select
                          value={selectedAssigneeId ?? ''}
                          onChange={(e) =>
                            setSelectedAssigneeId(e.target.value || null)
                          }
                          className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:[color-scheme:dark]"
                        >
                          <option value="" disabled>
                            {isScheduler
                              ? t('modal.selectAssigneeAdmin')
                              : t('modal.selectDoctor')}
                          </option>
                          {assignees.map((assignee) => (
                            <option key={assignee.id} value={assignee.id}>
                              {formatAssigneeLabel(assignee)}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-gray-400">
                          <FiCalendar size={12} />
                          {t('modal.date')}
                        </label>
                        <input
                          type="date"
                          value={formData.date}
                          onChange={(e) =>
                            setFormData({ ...formData, date: e.target.value })
                          }
                          className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:[color-scheme:dark]"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-gray-400">
                          <FiClock size={12} />
                          {t('modal.timeSlot')}
                        </label>
                        <select
                          value={formData.time}
                          onChange={(e) => {
                            userPickedTimeRef.current = true;
                            setFormData({ ...formData, time: e.target.value });
                          }}
                          className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:[color-scheme:dark]"
                        >
                          {timeOptions.map((slot) => (
                            <option
                              key={slot.time}
                              value={slot.time}
                              disabled={slot.isUnavailable}
                            >
                              {slot.time}
                              {slot.isBooked
                                ? t('modal.timeBookedHint', {
                                    defaultValue: t('modal.timeBooked'),
                                  })
                                : ''}
                              {!slot.isBooked && slot.isUnavailable
                                ? t('modal.timeUnavailable')
                                : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <p className="text-[11px] leading-relaxed text-slate-400">
                      {t('modal.timeBookedHintHelp', {
                        defaultValue:
                          'Slots marked Booked can still be selected for intentional double-booking.',
                      })}
                    </p>
                  </div>
                </section>

                <section className="space-y-1.5 pb-2">
                  <label className="block text-sm font-bold text-slate-800 dark:text-gray-100">
                    {t('modal.notes')}
                  </label>
                  <textarea
                    className="h-20 w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                    placeholder={t('modal.notesPlaceholder')}
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData({ ...formData, reason: e.target.value })
                    }
                  />
                </section>
              </div>
            </div>
          )}
        </div>

        <footer className="flex shrink-0 flex-col items-stretch justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.04)] dark:border-gray-700 dark:bg-gray-800 sm:flex-row sm:items-center sm:px-6 sm:py-4">
          <div className="text-xs text-slate-400">
            {isCreatingNew
              ? t('modal.savePatientThenBook', {
                  defaultValue: 'Save the patient to continue booking',
                })
              : canConfirm
                ? isEditing
                  ? t('modal.readyToUpdate', {
                      defaultValue: 'Ready to save changes',
                    })
                  : t('modal.readyToBook', {
                      defaultValue: 'Ready to confirm this appointment',
                    })
                : t('modal.completeRequired', {
                    defaultValue: 'Select a patient and time slot to continue',
                  })}
          </div>
          <div className="flex items-center justify-end gap-2">
            {isCreatingNew ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsCreatingNew(false)}
              >
                {t('modal.backToSearch', {
                  defaultValue: 'Back to patient search',
                })}
              </Button>
            ) : (
              <Button type="button" variant="ghost" size="sm" onClick={onClose}>
                {t('modal.discard')}
              </Button>
            )}
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={handleMainAction}
              disabled={
                ((!formData.patient && !isCreatingNew) ||
                  (!isCreatingNew && !resolvedAssigneeId)) ||
                isSaving
              }
              className="min-w-[160px] gap-2 sm:min-w-[180px]"
            >
              {isSaving
                ? t('modal.saving')
                : isCreatingNew
                  ? t('modal.savePatient')
                  : isEditing
                    ? t('modal.saveChanges', { defaultValue: 'Save Changes' })
                    : t('modal.confirmSlot')}
            </Button>
          </div>
        </footer>
      </div>
    </Modal>
  );
};

export default BookingWizardModal;
